import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Key, Link2, TrendingUp, BarChart3, FileText, FileBarChart, LogOut, ArrowLeftRight, PenLine, Share2, Megaphone, Video, Globe, Scale, Lock, CreditCard, Sparkles, Mail, Code, X, Palette, KeyRound, ClipboardList } from 'lucide-react';
import BrandLogo from './BrandLogo';
import { useState, useEffect } from 'react';
import axios from 'axios';
import AddonUnlockModal from './AddonUnlockModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { path: '/dashboard/growth-snapshot', label: 'Growth Opportunity Snapshot', icon: Sparkles },
  { path: '/dashboard/overview', label: 'Overview', icon: LayoutDashboard },
  { path: '/dashboard/keywords', label: 'Keywords & Queries', icon: Key },
  { path: '/dashboard/link-building', label: 'Link Building', icon: Link2 },
  { path: '/dashboard/position-tracking', label: 'Position Tracking', icon: TrendingUp },
  { path: '/dashboard/traffic-analytics', label: 'Traffic Analytics', icon: BarChart3 },
  { path: '/dashboard/content', label: 'Content & Assets', icon: FileText },
  { path: '/dashboard/email-marketing', label: 'Email Marketing', icon: Mail },
  { path: '/dashboard/reporting', label: 'Reporting', icon: FileBarChart }
];

export default function Sidebar({ user, onLogout, clientName, isViewingAsAdmin, onSwitchClient, planConfig, restricted = false, mobileOpen = false, onClose }) {
  const displayFirmName = clientName || user.firm?.name || 'Dashboard';
  const [firmLogo, setFirmLogo] = useState(null);
  const [hasBlogAccess, setHasBlogAccess] = useState(false);
  const [unlockAddonKey, setUnlockAddonKey] = useState(null);

  // Effective access map. For client we use their own firm.plan_config (or passed planConfig);
  // for admin/team viewing a client we use the planConfig prop. Admin/team without client view -> all enabled.
  const effectivePlan = planConfig || user.firm?.plan_config || null;
  const isClient = user.role === 'client';
  const addonState = (key) => {
    // Legal Translations is ALWAYS unlocked for clients (pay-per-document model)
    if (isClient && key === 'legal_translations') return 'enabled';
    if (!isClient && !planConfig) return 'enabled';  // admin viewing global -> show all
    if (!effectivePlan) return 'enabled';
    const raw = effectivePlan.addons_config?.[key];
    if (raw === 'enabled' || raw === 'locked' || raw === 'disabled' || raw === 'custom') return raw;
    // Legacy / null values default to 'enabled' for non-clients, 'locked' for clients
    if (!raw) return isClient ? 'locked' : 'enabled';
    return raw;
  };

  // Load logo from localStorage or user data
  useEffect(() => {
    const firmData = localStorage.getItem('firm');
    if (firmData) {
      try {
        const parsed = JSON.parse(firmData);
        if (parsed.logo) {
          setFirmLogo(parsed.logo);
          return;
        }
      } catch (e) {}
    }
    if (user?.firm?.logo) {
      setFirmLogo(user.firm.logo);
    }
  }, [user]);

  // Check blog access for non-client users
  useEffect(() => {
    if (user.role === 'client') {
      setHasBlogAccess(false);
      return;
    }
    if (user.role === 'super_admin') {
      setHasBlogAccess(true);
      return;
    }
    // Internal team - check via API
    const checkAccess = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/cms/blog-access`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasBlogAccess(res.data.has_access);
      } catch {
        setHasBlogAccess(false);
      }
    };
    checkAccess();
  }, [user]);

  const allNavItems = [
    ...navItems,
    ...(hasBlogAccess ? [{ path: '/dashboard/blogs', label: 'Blogs', icon: PenLine }] : [])
  ];

  const allAddOns = [
    { key: 'social_media', path: '/dashboard/social-media', label: 'Social Media', icon: Share2 },
    { key: 'paid_ads', path: '/dashboard/paid-ads', label: 'Paid Ads', icon: Megaphone },
    { key: 'ai_video', path: '/dashboard/ai-video', label: 'AI Video', icon: Video },
    { key: 'website_creation', path: '/dashboard/website-creation', label: 'Website Creation', icon: Globe },
    { key: 'product_dev', path: '/dashboard/web-dev', label: 'Product / Software Development', icon: Code }
  ];
  const addOnItems = allAddOns
    .map(it => ({ ...it, state: addonState(it.key) }))
    .filter(it => it.state !== 'disabled');

  const specialAddOns = [
    { key: 'legal_translations', path: '/dashboard/legal-translations', label: 'Legal Translations', icon: Scale }
  ]
    .map(it => ({ ...it, state: addonState(it.key) }))
    .filter(it => it.state !== 'disabled');
  
  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/50 z-[55] transition-opacity ${mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        data-testid="sidebar-overlay"
      />
      <aside
        className={`bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-white h-screen flex flex-col
          fixed inset-y-0 left-0 z-[60] w-[280px] max-w-[85vw] transform transition-transform duration-300
          lg:static lg:z-auto lg:w-auto lg:max-w-none lg:translate-x-0 lg:sticky lg:top-0
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="sidebar"
        onClick={(e) => { if (e.target.closest('a')) { onClose && onClose(); } }}
      >
        {/* Mobile close button */}
        <button
          type="button"
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 text-slate-300 hover:text-white z-10"
          aria-label="Close menu"
          data-testid="sidebar-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          {/* Show firm logo if available, otherwise default platform logo */}
          {firmLogo ? (
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-white flex items-center justify-center shadow-lg">
              <img 
                src={firmLogo} 
                alt={displayFirmName} 
                className="w-full h-full object-contain"
                data-testid="firm-logo"
              />
            </div>
          ) : (
            <BrandLogo size={40} />
          )}
          <div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent" style={{fontFamily: 'Outfit'}} data-testid="sidebar-logo">
              Law Agency 360
            </h1>
            <p className="text-xs text-slate-400 truncate max-w-[160px]" data-testid="sidebar-subtitle">
              {isViewingAsAdmin ? `Viewing: ${displayFirmName}` : 'Live Marketing Dashboard'}
            </p>
          </div>
        </div>
        {isViewingAsAdmin && (
          <div className="mt-3 px-3 py-1.5 bg-gradient-to-r from-violet-500/20 to-indigo-500/20 rounded-lg border border-violet-500/30">
            <span className="text-xs font-medium text-violet-300">
              {user.role === 'super_admin' ? 'Admin View' : 'Team View'}
            </span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
        {!restricted && allNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
                isActive
                  ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-600/30'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}

        {/* Add-ons section */}
        {!restricted && (
        <div className="pt-4 mt-2" data-testid="sidebar-addons-section">
          <div className="flex items-center gap-2 px-4 mb-2">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] font-semibold tracking-[0.15em] text-slate-500 uppercase">Add-ons</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          {addOnItems.map((item) => {
            const locked = item.state === 'locked';
            // Website Creation, Social Media + Paid Ads always navigate; the
            // paywall fires inside the tab. Other locked add-ons keep the
            // AddonUnlockModal behaviour.
            if (locked && !['website_creation','social_media','paid_ads','ai_video','product_dev'].includes(item.key)) {
              return (
                <button
                  key={item.path}
                  onClick={() => isClient && setUnlockAddonKey(item.key)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl text-slate-300 bg-white/[0.02] border border-white/5 hover:border-violet-500/40 hover:bg-violet-900/10 hover:text-white transition-all duration-200 cursor-pointer"
                  title="Click to view plans"
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}-locked`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  <Lock className="w-3.5 h-3.5 ml-auto text-amber-400/80" />
                </button>
              );
            }
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-600/30'
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}

          {/* Highlighted specialised add-ons (Legal Translations) */}
          {specialAddOns.length > 0 && (
            <>
              {/* Visual divider before specialised add-ons */}
              <div className="flex items-center gap-2 px-4 mt-5 mb-3" data-testid="sidebar-special-divider">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-violet-500/40 to-transparent" />
                <span className="text-[10px] font-semibold tracking-[0.15em] text-violet-300/80 uppercase">Specialised</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-violet-500/40 to-transparent" />
              </div>
              {specialAddOns.map((item) => {
                const locked = item.state === 'locked';
                if (locked) {
                  return (
                    <button
                      key={item.path}
                      onClick={() => isClient && setUnlockAddonKey(item.key)}
                      className="w-full relative flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl border border-violet-500/15 bg-gradient-to-r from-violet-900/25 to-indigo-900/15 text-violet-100 hover:from-violet-800/40 hover:to-indigo-800/30 hover:border-violet-400/40 hover:text-white transition-all duration-200 cursor-pointer"
                      title="Click to view plans"
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}-locked`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                      <Lock className="w-3.5 h-3.5 ml-auto text-amber-400/80" />
                    </button>
                  );
                }
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 border ${
                        isActive
                          ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-600/40 border-violet-400/60'
                          : 'text-violet-100 bg-gradient-to-r from-violet-900/40 to-indigo-900/30 border-violet-500/25 hover:border-violet-400/50 hover:from-violet-800/60 hover:to-indigo-800/50 hover:text-white shadow-md shadow-violet-900/20'
                      }`
                    }
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <span className="pointer-events-none absolute inset-0 rounded-xl bg-violet-500/5 blur-md -z-10" aria-hidden />
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                    <span className="ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-violet-500/30 text-violet-100 border border-violet-400/30">Legal</span>
                  </NavLink>
                );
              })}
            </>
          )}
        </div>
        )}

        {/* Client profile - admin/team viewing a client */}
        {!isClient && isViewingAsAdmin && (
          <div className="pt-4 mt-2" data-testid="sidebar-clientprofile-section">
            <div className="flex items-center gap-2 px-4 mb-2">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-[10px] font-semibold tracking-[0.15em] text-slate-500 uppercase">Client Profile</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <NavLink
              to="/dashboard/brand-priorities"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-600/30'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`
              }
              data-testid="nav-brand-priorities-admin"
            >
              <Palette className="w-5 h-5" />
              <span>Brand &amp; Priorities</span>
            </NavLink>
            <NavLink
              to="/dashboard/access"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-600/30'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`
              }
              data-testid="nav-access-admin"
            >
              <KeyRound className="w-5 h-5" />
              <span>Access</span>
            </NavLink>
            <NavLink
              to="/dashboard/onboarding-details"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-600/30'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`
              }
              data-testid="nav-onboarding-details"
            >
              <ClipboardList className="w-5 h-5" />
              <span>Onboarding Details</span>
            </NavLink>
          </div>
        )}

        {/* Billing & Plan - clients only (always visible, even when restricted) */}
        {isClient && (
          <div className="pt-4 mt-2" data-testid="sidebar-billing-section">
            <div className="flex items-center gap-2 px-4 mb-2">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-[10px] font-semibold tracking-[0.15em] text-slate-500 uppercase">Account</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <NavLink
              to="/dashboard/brand-priorities"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-600/30'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`
              }
              data-testid="nav-brand-priorities"
            >
              <Palette className="w-5 h-5" />
              <span>Brand &amp; Priorities</span>
            </NavLink>
            <NavLink
              to="/dashboard/access"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-600/30'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`
              }
              data-testid="nav-access"
            >
              <KeyRound className="w-5 h-5" />
              <span>Access</span>
            </NavLink>
            <NavLink
              to="/dashboard/billing"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-600/30'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`
              }
              data-testid="nav-billing-plan"
            >
              <CreditCard className="w-5 h-5" />
              <span>Billing & Plan</span>
            </NavLink>
          </div>
        )}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-white/10 bg-slate-950/50">
        {/* Switch Client Button for Admin/Team */}
        {isViewingAsAdmin && onSwitchClient && (
          <button
            onClick={onSwitchClient}
            className="flex items-center gap-2 w-full px-4 py-2.5 mb-3 text-sm font-medium text-violet-300 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-300 border border-violet-500/30 hover:border-violet-500/50"
            data-testid="sidebar-switch-client"
          >
            <ArrowLeftRight className="w-4 h-4" />
            <span>Switch Client</span>
          </button>
        )}
        {/* Back to Admin Panel when no client is selected (blog CMS mode) */}
        {!isViewingAsAdmin && user.role === 'super_admin' && (
          <NavLink
            to="/admin"
            className="flex items-center gap-2 w-full px-4 py-2.5 mb-3 text-sm font-medium text-violet-300 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-300 border border-violet-500/30 hover:border-violet-500/50"
            data-testid="back-to-admin"
          >
            <ArrowLeftRight className="w-4 h-4" />
            <span>Back to Admin Panel</span>
          </NavLink>
        )}
        {!isViewingAsAdmin && user.role === 'internal_team' && (
          <NavLink
            to="/my-clients"
            className="flex items-center gap-2 w-full px-4 py-2.5 mb-3 text-sm font-medium text-violet-300 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-300 border border-violet-500/30 hover:border-violet-500/50"
            data-testid="back-to-clients"
          >
            <ArrowLeftRight className="w-4 h-4" />
            <span>Back to My Clients</span>
          </NavLink>
        )}
        
        <div className="mb-3 p-3 rounded-xl bg-white/5">
          <div className="text-sm font-medium text-white truncate">{user.email}</div>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs text-slate-400">Role:</span>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-500/20 to-indigo-500/20 text-violet-300 capitalize">
              {user.role?.replace('_', ' ')}
            </span>
          </div>
          {!isViewingAsAdmin && user.role === 'client' && (
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-xs text-slate-400">Plan:</span>
              {effectivePlan?.plan_type ? (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-300 capitalize" data-testid="sidebar-plan-active">
                  {effectivePlan.plan_type}
                </span>
              ) : (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid="sidebar-plan-not-active">
                  Not Active
                </span>
              )}
            </div>
          )}
          {!isViewingAsAdmin && user.role !== 'client' && (effectivePlan?.plan_type || user.firm?.subscription_plan) && (
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-xs text-slate-400">Plan:</span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-300 capitalize">
                {effectivePlan?.plan_type || user.firm.subscription_plan}
              </span>
            </div>
          )}
        </div>
        <button
          onClick={onLogout}
          className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all duration-300"
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </button>
      </div>

      <AddonUnlockModal
        open={!!unlockAddonKey}
        addonKey={unlockAddonKey}
        onClose={() => setUnlockAddonKey(null)}
        onUnlocked={(res) => {
          // Update local user state and navigate to the just-unlocked module
          try {
            const stored = JSON.parse(localStorage.getItem('user') || '{}');
            if (stored?.firm) {
              stored.firm.plan_config = res.plan_config;
              localStorage.setItem('user', JSON.stringify(stored));
            }
          } catch (_e) { /* ignore */ }
          const targetMap = {
            social_media: '/dashboard/social-media',
            paid_ads: '/dashboard/paid-ads',
            ai_video: '/dashboard/ai-video',
            website_creation: '/dashboard/website-creation',
            legal_translations: '/dashboard/legal-translations'
          };
          setUnlockAddonKey(null);
          // Hard navigate so plan_config refresh applies everywhere
          if (targetMap[res.addon_key]) window.location.assign(targetMap[res.addon_key]);
        }}
      />
    </aside>
    </>
  );
}
